/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Grid,
  Typography,
} from '@mui/material';
import { Image } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setCurrentStudentId } from 'app/basicInformationSlice/basicInformationSlice';
import { get } from 'lodash';
import counselorServices from 'services/counselorServices';
import { routers } from 'router/router';
import { getStripeSubscriptionInfo } from 'app/basicInformationSlice/basicInformationSlice';
import { usePageStatus } from 'hooks/usePageStatus';
import { STRIPE_PRICE_NAME } from 'constants/common';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PeopleOutlineIcon from 'components/icons/PeopleOutlineIcon';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CheckIcon from '@mui/icons-material/Check';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';

const getPlanDisplayName = (plan: string | undefined) => {
  if (!plan) return 'Starter';
  switch (plan) {
    case STRIPE_PRICE_NAME.GoldMonth:
    case STRIPE_PRICE_NAME.GoldYear:
      return 'Gold';
    case STRIPE_PRICE_NAME.SilverPlusMonth:
    case STRIPE_PRICE_NAME.SilverPlusYear:
      return 'Silver Plus';
    case STRIPE_PRICE_NAME.SilverMonth:
    case STRIPE_PRICE_NAME.SilverYear:
      return 'Silver';
    case STRIPE_PRICE_NAME.GradMapStarter:
      return 'Starter';
    default:
      return plan;
  }
};

const ParentStudentProfilePage = () => {
  const params = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const studentId = params?.id === ':id' ? '' : params?.id ?? '';
  const personalStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const parentId = personalStore?.student_id;

  const [studentName, setStudentName] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [profileComplete, setProfileComplete] = useState(0);
  const [collegesSaved, setCollegesSaved] = useState(0);
  const [nextDeadline, setNextDeadline] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // usePageStatus gọi API page-status/student/:id và trả về pageStatus
  const { pageStatus } = usePageStatus({ studentId });

  useEffect(() => {
    const id = Number(studentId);
    if (!isNaN(id)) dispatch(setCurrentStudentId(id as any));
  }, [studentId, dispatch]);

  useEffect(() => {
    if (studentId && parentId) {
      counselorServices.getPartner(parentId).then((res: any) => {
        const child = (res ?? []).find((c: any) => String(c.student_id) === studentId);
        if (child?.student_info) {
          const name = [child.student_info.first_name, child.student_info.last_name]
            .filter(Boolean)
            .join(' ');
          setStudentName(name || 'Your Child');
        }
      });
      dispatch(getStripeSubscriptionInfo(studentId))
        .unwrap()
        .then((info: any) => setSubscriptionInfo(info))
        .catch(() => setSubscriptionInfo(null));
    }
  }, [studentId, parentId, dispatch]);

  // Tính Profile Complete từ API page-status/student/:id - tỷ lệ student_completed = true
  useEffect(() => {
    if (!pageStatus || pageStatus.length === 0) {
      setProfileComplete(0);
      return;
    }
    const completed = pageStatus.filter(
      (p: any) => p.student_completed === true || p.student_completed === 1
    ).length;
    const total = pageStatus.length;
    setProfileComplete(Math.round((completed / total) * 100));
  }, [pageStatus]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const planName = getPlanDisplayName(
    get(subscriptionInfo, 'subcription_plan') || get(subscriptionInfo, 'payload.subcription_plan')
  );

  const detailsPath = (routers.viewStudent?.path ?? '/application/counselor/:id').replace(':id', studentId);
  const goToDetailsTab = (tab: number) => {
    history.push(detailsPath + '?tab=' + tab);
  };

  const cardSx = {
    cursor: 'pointer',
    minWidth: isMobile ? '100px' : '140px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    p: 2,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    transition: 'all 0.2s',
    '&:hover': { borderColor: '#4d88f0', backgroundColor: '#f8faff', boxShadow: '0 2px 8px rgba(77,136,240,0.12)' },
  };

  const iconSize = 36;
  const iconBoxSx = { width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const gridItems = [
    { tab: 1, label: 'Profile', icon: <Box sx={iconBoxSx}><PersonOutlineOutlinedIcon sx={{ fontSize: iconSize, color: '#4d88f0' }} /></Box>, section: 'MY PROFILE' },
    { tab: 2, label: 'Family', icon: <Box sx={iconBoxSx}><PeopleOutlineIcon sx={{ fontSize: iconSize, color: '#4d88f0' }} /></Box>, section: 'MY PROFILE' },
    { tab: 3, label: 'Education', icon: <Box sx={iconBoxSx}><MenuBookOutlinedIcon sx={{ fontSize: iconSize, color: '#22c55e' }} /></Box>, section: 'MY PROFILE' },
    { tab: 4, label: 'Tests', icon: <Box sx={iconBoxSx}><Box sx={{ width: iconSize, height: iconSize, borderRadius: 1.5, bgcolor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(34, 197, 94, 0.4)' }}><CheckIcon sx={{ fontSize: 20, color: '#fff' }} /></Box></Box>, section: 'MY PROFILE' },
    { tab: 5, label: 'Courses & Grades', icon: <Box sx={iconBoxSx}><EditNoteOutlinedIcon sx={{ fontSize: iconSize, color: '#EAB308' }} /></Box>, section: 'MY PROFILE' },
    { tab: 6, label: 'Activities & Honors', icon: <Box sx={iconBoxSx}><EmojiEventsOutlinedIcon sx={{ fontSize: iconSize, color: '#EAB308' }} /></Box>, section: 'MY PROFILE' },
  ];

  const collegePlanningItems = [
    { label: 'Educational Programs', icon: <Box sx={iconBoxSx}><AutoAwesomeIcon sx={{ fontSize: iconSize, color: '#EAB308' }} /></Box>, onClick: () => history.push(routers.settings.path + '?tab=1') },
    { label: 'Colleges', icon: <Box sx={iconBoxSx}><AccountBalanceOutlinedIcon sx={{ fontSize: iconSize, color: '#92400e' }} /></Box>, onClick: () => goToDetailsTab(8) },
    { label: 'Auto-fill', icon: <Box sx={iconBoxSx}><BoltOutlinedIcon sx={{ fontSize: iconSize, color: '#EAB308' }} /></Box>, onClick: () => window.open('https://chrome.google.com/webstore/detail/gradmap/fbmaejeklcgdlohlngjkkggddapoklli?hl=en&authuser=0', '_blank') },
    { label: 'Summary', icon: <Box sx={iconBoxSx}><BarChartOutlinedIcon sx={{ fontSize: iconSize, color: '#4d88f0' }} /></Box>, onClick: () => history.push((routers.personalSum?.path ?? '/application/summary/personal/:id').replace(':id', studentId)) },
  ];

  const supportItems = [
    { label: 'Counselor', icon: <Box sx={iconBoxSx}><SchoolOutlinedIcon sx={{ fontSize: iconSize, color: '#374151' }} /></Box>, path: routers.settings.path + '?tab=3' },
    { label: 'Resources', icon: <Box sx={iconBoxSx}><LightbulbOutlinedIcon sx={{ fontSize: iconSize, color: '#EAB308' }} /></Box>, isExternal: true },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: isMobile ? 2 : 4, pb: 6 }}>
      <Box
        component="span"
        onClick={() => history.push(routers.parentMainDashboard?.path ?? '/dashboard/children')}
        sx={{ fontSize: 14, color: '#6c757d', cursor: 'pointer', textDecoration: 'underline', mb: 2, display: 'inline-block', '&:hover': { color: '#4d88f0' } }}
      >
        ← Back to dashboard
      </Box>

      {/* Banner */}
      <Box
        sx={{
          background: 'linear-gradient(90deg, #4d88f0 0%, #8cb5f3 100%)',
          borderRadius: '24px',
          color: 'white',
          p: 3,
          mb: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Stack direction={isMobile ? 'column' : 'row'} alignItems="center" spacing={3}>
          <Image src="./assets/images/Icon-01.svg" roundedCircle width={isMobile ? 64 : 80} />
          <Stack>
            <Typography variant="h5" fontWeight={700}>
              Hey {studentName || '...'}! Let&apos;s crush it today 💪
            </Typography>
            <Typography sx={{ opacity: 0.95, mt: 0.5 }}>
              You&apos;re making great progress on your college journey.
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Plan notification bar - ẩn khi đã Gold (plan cao nhất) */}
      {planName !== 'Gold' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#F3F4F6',
            borderRadius: '8px',
            px: 3,
            py: 2,
            mb: 3,
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 14, color: '#6c757d' }}>
            You are on the free {planName} plan. Invite a parent and upgrade to Silver or Gold to unlock parent access and counselor messaging.
          </Typography>
          <Box
            component="span"
            onClick={() => history.push((routers.parentUpgrade?.path ?? '/dashboard/upgrade') + '?studentId=' + studentId)}
            sx={{ fontSize: 14, color: '#4d88f0', cursor: 'pointer', fontWeight: 600, flexShrink: 0, '&:hover': { textDecoration: 'underline', color: '#3d78e0' } }}
          >
            Upgrade Now
          </Box>
        </Box>
      )}

      {/* Metrics / Progress cards - border like design */}
      <Stack direction="row" spacing={2} sx={{ mb: 4 }} flexWrap="wrap">
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            flex: 1,
            minWidth: 140,
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            p: 2,
          }}
        >
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(77, 136, 240, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrackChangesOutlinedIcon sx={{ fontSize: 24, color: '#4d88f0' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#374151', lineHeight: 1.2 }}>{profileComplete}%</Typography>
            <Typography variant="body2" color="text.secondary">Profile Complete</Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            flex: 1,
            minWidth: 140,
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            p: 2,
          }}
        >
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(77, 136, 240, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrendingUpOutlinedIcon sx={{ fontSize: 24, color: '#4d88f0' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#374151', lineHeight: 1.2 }}>{collegesSaved}</Typography>
            <Typography variant="body2" color="text.secondary">Colleges Saved</Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            flex: 1,
            minWidth: 140,
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            p: 2,
          }}
        >
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(239, 68, 68, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 24, color: '#ef4444' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#374151', lineHeight: 1.2 }}>{nextDeadline || '—'}</Typography>
            <Typography variant="body2" color="text.secondary">Next Deadline</Typography>
          </Box>
        </Stack>
      </Stack>

      {/* MY PROFILE + COLLEGE PLANNING + SUPPORT sections */}
      <Typography variant="overline" sx={{ fontWeight: 700, color: '#6b7280', letterSpacing: 1.5, display: 'block', mb: 2, fontSize: 12 }}>
        MY PROFILE
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {gridItems
          .filter((i) => i.section === 'MY PROFILE')
          .map((item) => (
            <Grid item xs={6} sm={4} md={2} key={item.tab}>
              <Box
                sx={cardSx}
                onClick={() => goToDetailsTab(item.tab)}
              >
                {item.icon}
                <Typography sx={{ mt: 1, fontSize: 13, fontWeight: 600, textAlign: 'center' }}>{item.label}</Typography>
              </Box>
            </Grid>
          ))}
      </Grid>

      <Typography variant="overline" sx={{ fontWeight: 700, color: '#6b7280', letterSpacing: 1.5, display: 'block', mb: 2, fontSize: 12 }}>
        COLLEGE PLANNING
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {collegePlanningItems.map((item) => (
          <Grid item xs={6} sm={4} md={2} key={item.label}>
            <Box sx={cardSx} onClick={item.onClick}>
              {item.icon}
              <Typography sx={{ mt: 1, fontSize: 13, fontWeight: 600, textAlign: 'center' }}>{item.label}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Typography variant="overline" sx={{ fontWeight: 700, color: '#6b7280', letterSpacing: 1.5, display: 'block', mb: 2, fontSize: 12 }}>
        SUPPORT
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {supportItems.map((item) => (
          <Grid item xs={6} sm={4} md={2} key={item.label}>
            <Box
              sx={cardSx}
              onClick={() => {
                if (item.isExternal) window.open('https://docs.gradmap.com/en', '_blank');
                else if ((item as any).path) history.push((item as any).path);
              }}
            >
              {item.icon}
              <Typography sx={{ mt: 1, fontSize: 13, fontWeight: 600, textAlign: 'center' }}>{item.label}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Typography sx={{ mt: 3, fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>
        Tip: Complete your Profile section to get personalized college recommendations!
      </Typography>
    </Box>
  );
};

export default ParentStudentProfilePage;
